/**
 * TELV1-535 — shrink oversized respondent photos before upload.
 *
 * Why this exists: the analyser sends respondent images to the vision model
 * as presigned URLs, and OpenAI refuses to fetch anything over ~20MB. A
 * modern phone camera clears that easily — the incident image was 27.64MB at
 * 4096x3072, a near-lossless JPEG. Re-encoded at quality 0.9 it was 2.29MB at
 * the SAME resolution, i.e. the bytes were pure compression overhead and
 * nothing of research value is lost by re-encoding.
 *
 * Uploads are capped at 200MB server-side (`multer.constants.ts`), which is
 * right for video but leaves images unbounded, so the fix belongs here where
 * we still have the decoded bitmap.
 *
 * Deliberately conservative: this sits on the live respondent send path, so
 * every failure mode returns the ORIGINAL file rather than blocking someone's
 * answer. A respondent must never lose their upload because a resize failed.
 */

/** Leave anything at or below this alone — most photos never touch this path. */
const RESIZE_THRESHOLD_BYTES = 8 * 1024 * 1024;

/**
 * Longest edge after downscaling. Far above what analysis consumes (the
 * vision model rescales to at most 2048px before tiling) and still generous
 * for a researcher zooming into a respondent's drawing.
 */
const MAX_LONG_EDGE = 4096;

const JPEG_QUALITY = 0.9;

/**
 * GIFs are left alone entirely. Canvas only ever captures the first frame, so
 * re-encoding an animated GIF would silently discard the animation — and a
 * respondent who uploads a GIF usually means the motion.
 */
const SKIP_TYPES = ['image/gif'];

/** Give up rather than hang the send path on a pathological decode. */
const DECODE_TIMEOUT_MS = 15_000;

function canDownscale(): boolean {
  return (
    typeof createImageBitmap === 'function' &&
    typeof document !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined'
  );
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => reject(new Error('image downscale timed out')), ms);
    }),
  ]);
}

/**
 * Returns a smaller JPEG when the input is a large image, otherwise the file
 * unchanged. Never throws.
 */
export async function downscaleImageIfNeeded(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (SKIP_TYPES.includes(file.type)) return file;
  if (file.size <= RESIZE_THRESHOLD_BYTES) return file;
  if (!canDownscale()) return file;

  let bitmap: ImageBitmap | undefined;

  try {
    // `imageOrientation: 'from-image'` is load-bearing. Canvas drops EXIF, so
    // without this a photo taken in portrait — which phones store as a
    // landscape buffer plus an orientation tag — would silently upload
    // rotated 90 degrees.
    bitmap = await withTimeout(
      createImageBitmap(file, { imageOrientation: 'from-image' }),
      DECODE_TIMEOUT_MS,
    );

    const { width, height } = bitmap;
    if (!width || !height) return file;

    const scale = Math.min(1, MAX_LONG_EDGE / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);

    // Try PNG first for PNG input. JPEG has no alpha channel, so re-encoding
    // a transparent PNG composites it onto the canvas's transparent-black
    // backdrop and the respondent's drawing comes back on a black
    // background.
    const isPng = file.type === 'image/png';

    const encode = (type: string): Promise<Blob | null> =>
      withTimeout(
        new Promise<Blob | null>((resolve) => {
          // toBlob ignores the quality argument for PNG (it is lossless).
          canvas.toBlob(resolve, type, JPEG_QUALITY);
        }),
        DECODE_TIMEOUT_MS,
      );

    let outputType = isPng ? 'image/png' : 'image/jpeg';
    let blob = await encode(outputType);

    // Lossless PNG cannot always get under the ceiling — a detailed
    // full-bleed image can re-encode no smaller than it started. Rather than
    // ship something the analyser will refuse to fetch, flatten onto WHITE
    // (paper, not the canvas's transparent black) and take the JPEG. Only
    // reached when PNG genuinely did not help.
    if (isPng && (!blob || blob.size > RESIZE_THRESHOLD_BYTES)) {
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, targetWidth, targetHeight);
      ctx.globalCompositeOperation = 'source-over';

      const jpegBlob = await encode('image/jpeg');
      if (jpegBlob && (!blob || jpegBlob.size < blob.size)) {
        blob = jpegBlob;
        outputType = 'image/jpeg';
      }
    }

    // If re-encoding produced nothing smaller, keep the original.
    if (!blob || blob.size >= file.size) return file;

    // Keep the extension honest: the server derives the S3 key from
    // originalname, so a mislabelled file is misleading in exports and
    // downloads.
    const baseName = file.name.replace(/\.[^./\\]+$/, '') || 'image';
    const extension = outputType === 'image/png' ? 'png' : 'jpg';

    return new File([blob], `${baseName}.${extension}`, {
      type: outputType,
      lastModified: file.lastModified,
    });
  } catch {
    // Decode failures are real on some iOS Safari/HEIC combinations and on
    // corrupt EXIF. Upload what the respondent gave us.
    return file;
  } finally {
    bitmap?.close?.();
  }
}
